"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseOrCADCaptureProject = parseOrCADCaptureProject;
exports.normalizePropertyName = normalizePropertyName;
exports.getProperty = getProperty;
const NodeParser_1 = require("../../../common/NodeParser");
const BoundaryLevel_1 = require("@cadstrom/sigma-engine-shared/dist/BoundaryLevel");
const OrCADParsingResult_1 = require("../../common/OrCADParsingResult");
const KNOWN_DESIGN_RESOURCES_PROPERTIES = new Set([
    'BuildFileAddedOrDeleted',
    'CompileFileAddedOrDeleted',
    'NoModify',
    '"Create Allegro Netlist"',
    '"Allegro Netlist Directory"',
    '"View Allegro Netlist Files"',
    '"Allegro Netlist Create DCF File"',
    '"Update Allegro Board"',
    '"Allegro Netlist Input Board File"',
    '"Allegro Netlist Output Board File"',
    '"Allegro Netlist Remove Etch"',
    '"Allegro Netlist Place Changed Component"',
    '"Allegro Netlist Open Board in Allegro"',
    '"Allegro Setup Backup Versions"',
    '"Allegro Setup Output Warnings"',
    '"Allegro Setup Ignore Constraints"',
    '"Allegro Setup Part Type Length"',
    '"Allegro Netlist Combine Property String"',
    '"Allegro Netlist Ignore Fixed Property"',
    '"Allegro Netlist User Defined Property"',
    'Netlist_TAB',
    '"Allegro Setup Configuration File"',
    'DOCKED',
    'DOCKING_POSITION',
    'Board_sim_option',
    'OTHER_Part_Value',
    'OTHER_Netlist_File',
    'OTHER_Netlist_File2',
    'OTHER_View_Output',
    'OTHER_View_Output2',
    'OTHER_Formatter',
    'OTHER_PCB_Footprint',
    'OTHER_Switch0',
    'OTHER_Switch1',
    'OTHER_Switch2',
    'OTHER_Switch3',
    'OTHER_Switch4',
    'OTHER_Switch5',
    'OTHER_Switch6',
]);
const KNOWN_TOP_LEVEL_NODES = new Set([
    'ProjectVersion',
    'SoftwareVersion',
    'ProjectType',
    'Folder',
    'PartMRUSelector',
    'GlobalState',
    'MPSSessionName',
    'ISPCBBASICLICENSE',
]);
function parseOrCADCaptureProject(contents) {
    const rootNode = NodeParser_1.SExpressionFileParser.Parse(contents);
    if (rootNode.name !== 'ExpressProject') {
        throw new Error('Invalid OrCAD Capture project file: root node must be ExpressProject');
    }
    const context = {
        warnings: [],
    };
    const project = {
        folders: [],
    };
    const processingResults = {
        type: 'orcad-parsing',
        message: 'OrCAD project parsing completed',
        innerResults: [],
        severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
    };
    for (const node of rootNode.nodes) {
        switch (node.name) {
            case 'ProjectVersion':
                project.projectVersion = parseQuotedValue(node.values[0]);
                break;
            case 'SoftwareVersion':
                project.softwareVersion = parseQuotedValue(node.values[0]);
                break;
            case 'ProjectType':
                project.projectType = parseQuotedValue(node.values[0]);
                break;
            case 'Folder':
                project.folders.push(parseFolder(node, context));
                break;
            case 'PartMRUSelector':
                project.partMRUSelector = parsePartMRUSelector(node, context);
                break;
            case 'GlobalState':
                project.globalState = parseGlobalState(node, context);
                break;
            case 'MPSSessionName':
                project.mpsSessionName = parseQuotedValue(node.values[0]);
                break;
            case 'ISPCBBASICLICENSE':
                project.isPCBBasicLicense = parseQuotedValue(node.values[0]) === 'true';
                break;
            default:
                if (!KNOWN_TOP_LEVEL_NODES.has(node.name)) {
                    context.warnings.push({
                        type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
                        message: `Unknown top-level node: ${node.name}`,
                        severity: BoundaryLevel_1.BoundaryLevel.Warning,
                        nodeName: node.name,
                        nodeValues: node.values,
                    });
                }
                break;
        }
    }
    processingResults.innerResults = context.warnings;
    if (context.warnings.length > 0) {
        const hasErrors = context.warnings.some((w) => w.severity === BoundaryLevel_1.BoundaryLevel.Error);
        const hasWarnings = context.warnings.some((w) => w.severity === BoundaryLevel_1.BoundaryLevel.Warning);
        processingResults.severity =
            hasErrors ? BoundaryLevel_1.BoundaryLevel.Error
                : hasWarnings ? BoundaryLevel_1.BoundaryLevel.Warning
                    : BoundaryLevel_1.BoundaryLevel.Recommendation;
        processingResults.message = `OrCAD project parsing completed with ${context.warnings.length} warning(s)`;
    }
    else {
        processingResults.severity = BoundaryLevel_1.BoundaryLevel.Recommendation;
    }
    return {
        project,
        processingResults,
    };
}
function parseFolder(node, context) {
    const folder = {
        name: parseQuotedValue(node.values[0]) || '',
        folders: [],
        files: [],
        properties: {},
    };
    const folderName = folder.name;
    const isDesignResourcesFolder = folderName === 'Design Resources';
    for (const childNode of node.nodes) {
        switch (childNode.name) {
            case 'Folder':
                folder.folders.push(parseFolder(childNode, context));
                break;
            case 'File':
                folder.files.push(parseFile(childNode, context));
                break;
            case 'Sort':
                folder.sort = parseQuotedValue(childNode.values[0]);
                break;
            default:
                if (childNode.values.length > 0) {
                    const key = parseQuotedValue(childNode.name) || childNode.name;
                    const value = parseQuotedValue(childNode.values[0]);
                    folder.properties[key] = value;
                    const checkKey = childNode.name;
                    if (isDesignResourcesFolder && !KNOWN_DESIGN_RESOURCES_PROPERTIES.has(checkKey)) {
                        context.warnings.push({
                            type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownProperty,
                            message: `Unknown property in Design Resources folder: ${key}`,
                            severity: BoundaryLevel_1.BoundaryLevel.Warning,
                            folderName,
                            propertyName: key,
                            propertyValue: value,
                        });
                    }
                }
                else {
                    const key = parseQuotedValue(childNode.name) || childNode.name;
                    folder.properties[key] = true;
                    const checkKey = childNode.name;
                    if (isDesignResourcesFolder && !KNOWN_DESIGN_RESOURCES_PROPERTIES.has(checkKey)) {
                        context.warnings.push({
                            type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownProperty,
                            message: `Unknown boolean property in Design Resources folder: ${key}`,
                            severity: BoundaryLevel_1.BoundaryLevel.Warning,
                            folderName,
                            propertyName: key,
                            propertyValue: true,
                        });
                    }
                }
                break;
        }
    }
    return folder;
}
function parseFile(node, context) {
    const file = {
        path: parseQuotedValue(node.values[0]) || '',
        type: '',
    };
    for (const childNode of node.nodes) {
        if (childNode.name === 'Type') {
            file.type = parseQuotedValue(childNode.values[0]) || '';
        }
        else {
            context.warnings.push({
                type: 'orcad-unknown-property',
                message: `Unknown file property: ${childNode.name}`,
                severity: BoundaryLevel_1.BoundaryLevel.Warning,
                fileName: file.path,
                propertyName: childNode.name,
            });
        }
    }
    return file;
}
function parsePartMRUSelector(node, context) {
    const selector = {
        properties: {},
    };
    for (const childNode of node.nodes) {
        if (childNode.values.length > 0) {
            selector.properties[childNode.name] = parseQuotedValue(childNode.values[0]);
        }
        else {
            context.warnings.push({
                type: 'orcad-unknown-node',
                message: `Unexpected content in PartMRUSelector: ${childNode.name}`,
                severity: BoundaryLevel_1.BoundaryLevel.Warning,
                nodeName: childNode.name,
            });
        }
    }
    return selector;
}
function parseGlobalState(node, context) {
    const globalState = {
        documents: [],
    };
    for (const childNode of node.nodes) {
        switch (childNode.name) {
            case 'FileView':
                globalState.fileView = parseFileView(childNode, context);
                break;
            case 'HierarchyView':
                globalState.hierarchyView = { properties: {} };
                break;
            case 'Doc':
                globalState.documents.push(parseDocument(childNode, context));
                break;
            default:
                context.warnings.push({
                    type: 'orcad-unknown-node',
                    message: `Unknown GlobalState content: ${childNode.name}`,
                    severity: BoundaryLevel_1.BoundaryLevel.Warning,
                    nodeName: childNode.name,
                });
                break;
        }
    }
    return globalState;
}
function parseFileView(node, context) {
    const fileView = {
        paths: [],
    };
    for (const childNode of node.nodes) {
        switch (childNode.name) {
            case 'Path': {
                fileView.paths.push(parseFileViewPath(childNode, context));
                break;
            }
            case 'Select': {
                fileView.selection = {
                    path: childNode.values.map((v) => parseQuotedValue(v)),
                };
                break;
            }
            default:
                context.warnings.push({
                    type: 'orcad-unknown-node',
                    message: `Unknown FileView content: ${childNode.name}`,
                    severity: BoundaryLevel_1.BoundaryLevel.Warning,
                    nodeName: childNode.name,
                });
                break;
        }
    }
    return fileView;
}
function parseFileViewPath(node, context) {
    const [editorFolder, fsPath, ...folderPosition] = node.values;
    const path = {
        editorFolder: parseQuotedValue(editorFolder),
        fsPath: fsPath ? parseQuotedValue(fsPath) : undefined,
        folderPosition: folderPosition.map((v) => parseQuotedValue(v)),
    };
    return path;
}
function parseDocument(node, context) {
    const document = {
        type: '',
    };
    for (const childNode of node.nodes) {
        switch (childNode.name) {
            case 'Type':
                document.type = parseQuotedValue(childNode.values[0]) || '';
                break;
            case 'Frame':
                document.frame = parseDocumentFrame(childNode, context);
                break;
            case 'Tab':
                document.tab = parseInt(childNode.values[0], 10);
                break;
            case 'Path':
                document.path = parseQuotedValue(childNode.values[0]);
                break;
            case 'Schematic':
                document.schematic = parseQuotedValue(childNode.values[0]);
                break;
            case 'Page':
                document.page = parseQuotedValue(childNode.values[0]);
                break;
            default:
                context.warnings.push({
                    type: 'orcad-unknown-node',
                    message: `Unknown Document content: ${childNode.name}`,
                    severity: BoundaryLevel_1.BoundaryLevel.Warning,
                    nodeName: childNode.name,
                });
                break;
        }
    }
    return document;
}
function parseDocumentFrame(node, context) {
    const frame = {};
    for (const childNode of node.nodes) {
        switch (childNode.name) {
            case 'Placement':
                frame.placement = parseQuotedValue(childNode.values[0]);
                break;
            case 'Scroll':
                frame.scroll = parseQuotedValue(childNode.values[0]);
                break;
            case 'Zoom':
                frame.zoom = parseQuotedValue(childNode.values[0]);
                break;
            case 'Occurrence':
                frame.occurrence = parseQuotedValue(childNode.values[0]);
                break;
            default:
                context.warnings.push({
                    type: 'orcad-unknown-node',
                    message: `Unknown DocumentFrame content: ${childNode.name}`,
                    severity: BoundaryLevel_1.BoundaryLevel.Warning,
                    nodeName: childNode.name,
                });
                break;
        }
    }
    return frame;
}
function parseQuotedValue(value) {
    if (!value) {
        return '';
    }
    if (value.startsWith('"') && value.endsWith('"')) {
        return value.slice(1, -1);
    }
    return value;
}
function normalizePropertyName(name) {
    return parseQuotedValue(name);
}
function getProperty(properties, key) {
    if (Object.prototype.hasOwnProperty.call(properties, key)) {
        return properties[key];
    }
    const quotedKey = `"${key}"`;
    if (Object.prototype.hasOwnProperty.call(properties, quotedKey)) {
        return properties[quotedKey];
    }
    if (key.startsWith('"') && key.endsWith('"')) {
        const unquotedKey = key.slice(1, -1);
        if (Object.prototype.hasOwnProperty.call(properties, unquotedKey)) {
            return properties[unquotedKey];
        }
    }
    return undefined;
}
//# sourceMappingURL=OPJParser.js.map